import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
// Fixed import paths - make sure these match your actual file structure
import { clearPasswordStatus } from "../../store/auth/authSlice";
import {
  updateUserPassword,
  validateCurrentPassword
} from "../../store/auth/authActions";

const ResetPasswordModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const {
    user,
    validatingPassword,
    passwordValidationError,
    updatingPassword,
    updateSuccess,
    updateError
  } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    previousPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    previous: false,
    new: false,
    confirm: false
  });
  const [previousPasswordVerified, setPreviousPasswordVerified] =
    useState(false);

  // Debounced password validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.previousPassword.length >= 6 && user?.email) {
        dispatch(
          validateCurrentPassword({
            email: user.email,
            currentPassword: formData.previousPassword
          })
        )
          .unwrap()
          .then(() => setPreviousPasswordVerified(true))
          .catch(() => setPreviousPasswordVerified(false));
      } else {
        setPreviousPasswordVerified(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [formData.previousPassword, user?.email, dispatch]);

  useEffect(() => {
    if (!open) {
      setFormData({
        previousPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setPreviousPasswordVerified(false);
      dispatch(clearPasswordStatus());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated.",
        variant: "default"
      });
      onClose();
    }
    if (updateError) {
      toast({
        title: "Password Update Failed",
        description: updateError,
        variant: "destructive"
      });
    }
  }, [updateSuccess, updateError, toast, onClose]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Reset verification status when previous password changes
    if (field === "previousPassword") {
      setPreviousPasswordVerified(false);
    }
  };

  const handleUpdatePassword = () => {
    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: "Validation Error",
        description: "New passwords do not match.",
        variant: "destructive"
      });
      return;
    }

    // Validate new password length
    if (formData.newPassword.length < 6) {
      toast({
        title: "Validation Error",
        description: "New password must be at least 6 characters long.",
        variant: "destructive"
      });
      return;
    }

    // Check if new password is different from current
    if (formData.newPassword === formData.previousPassword) {
      toast({
        title: "Validation Error",
        description: "New password must be different from current password.",
        variant: "destructive"
      });
      return;
    }

    dispatch(
      updateUserPassword({
        currentPassword: formData.previousPassword,
        newPassword: formData.newPassword
      })
    );
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const isFormValid =
    previousPasswordVerified &&
    formData.newPassword.length >= 6 &&
    formData.newPassword === formData.confirmPassword &&
    formData.newPassword !== formData.previousPassword;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Lock className="h-5 w-5 text-accent" />
            Change Password
          </DialogTitle>
        </DialogHeader>

        <motion.div className="space-y-6 p-4">
          <PasswordField
            id="previousPassword"
            label="Current Password"
            value={formData.previousPassword}
            onChange={(val) => handleInputChange("previousPassword", val)}
            show={showPasswords.previous}
            toggleShow={() => togglePasswordVisibility("previous")}
            validation={{
              isValid: previousPasswordVerified,
              message:
                (passwordValidationError &&
                  "The password you entered does not match your current password.") ||
                (previousPasswordVerified ? "Password verified" : "")
            }}
            isLoading={validatingPassword}
            disabled={validatingPassword || updatingPassword}
          />

          <PasswordField
            id="newPassword"
            label="New Password"
            value={formData.newPassword}
            onChange={(val) => handleInputChange("newPassword", val)}
            show={showPasswords.new}
            toggleShow={() => togglePasswordVisibility("new")}
            validation={{
              isValid:
                formData.newPassword.length >= 6 &&
                formData.newPassword !== formData.previousPassword &&
                /[A-Z]/.test(formData.newPassword) &&
                /[a-z]/.test(formData.newPassword) &&
                /[0-9]/.test(formData.newPassword) &&
                /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword),
              message:
                formData.newPassword.length === 0
                  ? ""
                  : formData.newPassword.length < 6
                  ? "Password must be at least 6 characters long"
                  : formData.newPassword === formData.previousPassword
                  ? "New password must be different from current password"
                  : !/[A-Z]/.test(formData.newPassword)
                  ? "Password must contain at least one uppercase letter"
                  : !/[a-z]/.test(formData.newPassword)
                  ? "Password must contain at least one lowercase letter"
                  : !/[0-9]/.test(formData.newPassword)
                  ? "Password must contain at least one number"
                  : !/[!@#$%^&*(),.?\":{}|<>]/.test(formData.newPassword)
                  ? "Password must include at least one special character (!@#$%^&*)"
                  : ""
            }}
            disabled={updatingPassword}
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm New Password"
            value={formData.confirmPassword}
            onChange={(val) => handleInputChange("confirmPassword", val)}
            show={showPasswords.confirm}
            toggleShow={() => togglePasswordVisibility("confirm")}
            validation={{
              isValid:
                formData.newPassword === formData.confirmPassword &&
                !!formData.confirmPassword,
              message:
                formData.confirmPassword &&
                formData.newPassword !== formData.confirmPassword
                  ? "Passwords do not match"
                  : ""
            }}
            disabled={updatingPassword}
          />
        </motion.div>

        <DialogFooter>
          <div className="flex gap-2">
            <Button
              onClick={handleUpdatePassword}
              disabled={!isFormValid || validatingPassword || updatingPassword}
              variant="outline"
              className="flex-1"
            >
              {updatingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" /> Update Password
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PasswordField = ({
  id,
  label,
  value,
  onChange,
  show,
  toggleShow,
  validation,
  isLoading,
  disabled
}) => {
  const getValidationIcon = () => {
    if (isLoading)
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (validation.isValid === true)
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (validation.isValid === false && validation.message)
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    return null;
  };

  const getValidationColor = () => {
    if (validation.isValid === true) return "text-green-400";
    if (validation.isValid === false && validation.message)
      return "text-red-400";
    return "text-muted-foreground";
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-gray-200">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          placeholder={`Enter ${label.toLowerCase()}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="pr-20 px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-accent w-full"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {getValidationIcon()}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 hover:bg-transparent"
            onClick={toggleShow}
            disabled={disabled}
          >
            {show ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
      </div>
      <AnimatePresence>
        {validation.message && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`text-xs ${getValidationColor()}`}
          >
            {validation.message}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResetPasswordModal;
